/// <reference path="../typings/tsd.d.ts" />

import 'es5-shim'

import _ from "underscore";
import m from "mithril";

import Api from './api';
import EventReporter from './eventReporter';
import {Questionary} from './model/questionary';

import {Application} from './ui/app';
import customization = require('./customization/customization');
import Messages from './model/messages';
import {request} from './model/utils';

import {Promise} from "es6-promise";
import {Form} from "./model/form/form";

//--------------------------------
// CONFIGURE SENTRY
import * as Sentry from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";
import TFeedbackDefinition from "./model/feedback/TFeedbackDefinition";

if (process.env.NODE_ENV === 'production') {
    Sentry.init({
        dsn: "https://e279c8c2bc9947c0842919d5f08faa3e@o1168082.ingest.sentry.io/6535610",
        integrations: [new BrowserTracing()],
        environment: process.env.NODE_ENV,
        tracesSampleRate: 0.0,
        ignoreErrors: [
            /__gCrWeb\.instantSearch/, // ios chrome __gCrWeb @see https://stackoverflow.com/questions/26483541/referenceerror-cant-find-variable-gcrweb
        ]
    });
}


interface Options {
    evaluatePreFilter: any;
	questionaryId:string;
	projectId:string;
	key:string;
	apiUrl:string;

	messages:{ [key:string]:string };
	multilangMessages: {[key: string]:{[key: string]: string}};

	showProgressbar:boolean;
	showQuestionNumber: boolean;
	forceVisibility?: {
		nextButton?: "show"|"hide",
		backButton?: "show"|"hide"
	};
	respondentId?: string;
	pageAnimation?: string;
	presetVariables?: {[variable:string]: string|number};
	firstForm?: string;
    test?: boolean;
    questionaryVersion?: number;

    feedback?: TFeedbackDefinition,
	// debug:
	api?: Api;
	definitionUrl?: string;
    disableSentry?: boolean;
}

interface Callback {
    event: string;
    callback: (any?:any) => void;
}


// survey API
class Survey {

	options:Options;
	initialized = false;
	callbacksToRegister: Callback[] = [];

	private questionary: Questionary|null = null;
	private showProgressbar: boolean = true;
	private pageAnimation : string = 'move';
	private messages!: Messages;
	private api?: Api;
    public app?: Application;

	constructor(options:Options) {
		this.options = options;
	}

	init() {
	    const self = this;

        if (this.options?.disableSentry && Sentry.getCurrentHub().getClient()) {
            Sentry.close().then(() => console.log('Sentry was disabled.'));
        }

		this.api = this.options.api ? self.options.api : new Api({
			key: this.options.key,
			questionaryId: this.options.questionaryId,
			projectId: this.options.projectId,
			baseUrl: this.options.apiUrl,
            respondentId: this.options.respondentId,
            questionaryVersion: this.options.questionaryVersion,
		});

		if (this.api !== undefined) {

		    const api = this.api;

            // mock api for local definitions
            if (!this.options.key && !this.options.api) {

                // @ts-ignore  cos it is only mocking
                api.securedRequest = () => {

                    const promise = new Promise(resolve => {
                        setTimeout(resolve.bind(promise, {}), 100);
                    });
                    return promise;
                };

                api.getDefinition = () => request({url: this.options.definitionUrl + '/questionary.xml', dataType: 'text'});
                api.getFileUrl = name => this.options.definitionUrl + '/img/' + name;
            }

            const eventReporter = new EventReporter(api);

            this.messages = new Messages(this.options.messages);
            if (this.options.multilangMessages) {
                this.messages!.multilang(this.options.multilangMessages)
            }



            this.questionary = new Questionary(api, this.messages, eventReporter);
                if( this.options.firstForm ) {
                    this.questionary.setPresetFirstForm(this.options.firstForm);
                }
            }


		this.showProgressbar = this.options.showProgressbar;
		this.pageAnimation = this.options.pageAnimation || 'move';
        this.questionary!.messages = this.messages; // TODO: Odstarnit zvojenou referenci
        this.questionary!.test = this.options.test || false;

		// Process registeredCallbacks
		_.each(this.callbacksToRegister, (cb: Callback) => this.doRegisterCallback(cb.event, cb.callback));
		this.initialized = true;
	}

	/**
	 * Initialize and start the Survey. This method should be called just once.
	 */
	start(element: Node|string) {
		this.init();
		return this.questionary!.init(this.options.presetVariables).then(() => {
			this.app = Application.run(this.questionary!, {
				element: element,
				showProgressbar: this.showProgressbar,
                showQuestionNumber: Boolean(this.options.showQuestionNumber),
				pageAnimation: this.pageAnimation,
				messages: this.messages!,
				forceVisibility: this.options.forceVisibility || {},
                feedback: this.options.feedback,
			});
		});
	}

	getProgress() {
	    return this.questionary?.progress;
	}

	getCurrentForm() {
	    return this.questionary?.currentForm?.id || null;
	}

	getQuestionaryInfo() {
		const formatForm = (form: Form) => ({
			id: form.id,
			number: form.getNumber(),
			text: this.questionary!.renderText( form.text ),
			hint: form.hint
		});

		const formMap = _.mapObject(this.questionary!.formsById, (form: Form) => formatForm(form));
		const formList = _.map(this.questionary!.getFormList(), formatForm);

		return {
			formMap: formMap,
			formList: formList
		}
	}

	showForm(id: string, options?: Options) {
	    if (!this.questionary) {
	        return;
        }
		const noJumpAndPreFilter = !(options ? options.evaluatePreFilter : false);

		if (this.questionary.isInitialized) {
			this.questionary.goTo(id, noJumpAndPreFilter);
			m.redraw();
		} else {
			// postpone changing form until questionary is initialized
			this.questionary.on(Questionary.Events.QUESTIONARY_INITIALIZED, this.showForm.bind(this, id));
		}
	}

	// supported options: messages
	setOptions(options: Options) {
		_.extend(this.options, options);
	}


	setLang(lang: string) {
	    if (this.questionary && this.questionary.dictionary) {
	        this.questionary.dictionary.locale = lang;
	        this.messages!.setLocale(lang);
	        this.questionary && this.questionary.trigger(Questionary.Events.QUESTIONARY_LANGUAGE_CHANGED, lang);
	        m.redraw();
        } else {
	        alert('This project has not multi-language support.');
        }
    }

	registerCallback(event: string, callback:(any?:any) => void) {
		if (!this.initialized) {
			this.callbacksToRegister.push({
				event: event,
				callback: callback
			});
		} else {
			this.doRegisterCallback(event,callback);
		}
	}

	removeCallback(event:string, callback:(any?:any) => void) {
		this.questionary!.off(event, callback);
	}

	registerFormCustomization(id: string, dependencies: string[], factory: any) {
		customization.register('form', id, dependencies, factory);
	}

	private doRegisterCallback(event:string, callback:(any?:any) => void) {
		this.questionary!.on(event, callback);
	}
}

export = Survey;